import {
  Cpu,
  Zap,
  HeartPulse,
  Users,
  TrendingUp,
  Layers,
  Handshake,
  DollarSign,
  ArrowUpRight,
  Rocket,
} from "lucide-react";
import { ASSETS } from "@/data/assets";

const IMPACT = [
  {
    icon: TrendingUp,
    metric: "$37M+",
    label: "Run‑rate revenue",
    body: "Scaling revenue from early‑stage to over $37M run‑rate in regional turnarounds.",
  },
  {
    icon: ArrowUpRight,
    metric: "3× growth",
    label: "Account expansion",
    body: "Achieved 3× account growth and multi‑million dollar exits in startup environments.",
  },
  {
    icon: Rocket,
    metric: "2‑digit %",
    label: "Margin expansion",
    body: "Drove double‑digit percentage margin expansion across multiple business units in competitive markets.",
  },
];

const ROLES = [
  {
    company: "International SOS",
    role: "Vice President of Sales, North America Medical Services",
    period: "2024 — 2026",
    tag: "Medical Services",
    icon: HeartPulse,
    accent: "ai",
    image: null,
    body: "Recruited to turn around and expand medical staffing and occupational health services in North America after several years of limited success. Built scalable sales processes, restructured the go‑to‑market strategy, implemented CRM and proposal systems, and hired and developed the sales team. Focused on targeting the right segments and bringing proven practices from other industries to better serve buyers in infrastructure and energy projects. Delivered substantial revenue growth and meaningful profitability improvement in a highly competitive market.",
    highlights: [
      "Turnaround & expansion",
      "GTM restructure",
      "CRM & proposal systems",
      "Team hiring & development",
    ],
  },
  {
    company: "AspenTech",
    role: "Sales Director, North America",
    period: "2020 — 2024",
    tag: "Technology · Industrial Software",
    icon: Cpu,
    accent: "accent",
    image: null,
    body: "Directed sales growth for the High Velocity segment, managing a large book of business and leading the transition to AI/ML‑driven industrial automation and SaaS solutions. Delivered substantial revenue through complex enterprise cycles.",
    highlights: [
      "AI/ML industrial automation",
      "SaaS transition",
      "Complex enterprise cycles",
    ],
  },
  {
    company: "IOT‑eq",
    role: "Co‑Founder & Executive Vice President",
    period: "2016 — 2021",
    tag: "Technology · Industrial IoT Startup",
    icon: Rocket,
    accent: "accent",
    image: null,
    body: "Co‑founded and led the commercial strategy for an industrial IoT startup. Built the sales organization, developed strategic technology partnerships, and navigated significant challenges during COVID‑19. Successfully exited the business through a sale to one of our key customers, delivering strong revenue growth and a positive outcome for stakeholders.",
    highlights: [
      "Founder‑led sales",
      "Strategic partnerships",
      "Successful exit",
    ],
  },
  {
    company: "Baker Hughes",
    role: "Earlier Leadership Roles in Energy",
    period: "Foundation years",
    tag: "Energy · Oil & Gas",
    icon: Zap,
    accent: "energy",
    image: ASSETS.onsite,
    imageAlt: "On‑site with the team — enterprise energy program in action",
    imageCaption: "On‑site with the customer",
    imagePosition: "50% 30%",
    body: "Progressed through account management, project leadership, and sales management positions, consistently delivering major contracts and exceeding targets in Oil & Gas.",
    highlights: [
      "Account management",
      "Project leadership",
      "Major contract wins",
    ],
  },
];

const THEMES = [
  {
    icon: TrendingUp,
    title: "Scale revenue & teams",
    body: "Proven ability to scale revenue and teams in competitive, technical markets.",
  },
  {
    icon: Users,
    title: "Solution selling depth",
    body: "Deep expertise in solution selling, stakeholder management, and margin‑focused growth.",
  },
  {
    icon: Layers,
    title: "Adaptive to change",
    body: "Consistent track record of adapting to changing technology and market conditions.",
  },
];

export default function Experience() {
  return (
    <div data-testid="page-experience">
      {/* Full-width Client Visit banner */}
      <section
        data-testid="experience-banner"
        className="relative w-full h-[42vh] sm:h-[52vh] lg:h-[62vh] min-h-[320px] max-h-[720px] overflow-hidden border-b border-border"
      >
        <img
          src={ASSETS.clientVisit}
          alt="Vincent shaking hands with a client during an on-site visit"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "50% 34%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/70 via-background/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        <div className="hairline-ai absolute inset-x-0 bottom-0" />

        <div className="absolute inset-0 flex items-end">
          <div className="mx-auto max-w-7xl w-full px-6 lg:px-10 pb-10 lg:pb-16">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent flex items-center gap-2">
              <Handshake className="h-3.5 w-3.5" />
              // Client Visit
            </p>
            <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-4xl">
              Where trust is built —{" "}
              <span className="text-muted-foreground">in the room, on the ground.</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 lg:pt-24">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
          // Experience
        </p>
        <h2 className="mt-4 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] max-w-4xl">
          20+ years leading sales & growth across{" "}
          <span className="text-muted-foreground">
            Energy, Technology, and Medical Services.
          </span>
        </h2>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
          I've spent over 20 years leading sales organizations and driving
          growth in complex B2B environments across Energy, Technology, and
          Medical Services.
        </p>
      </section>

      {/* KEY FINANCIAL IMPACT */}
      <section
        data-testid="key-financial-impact"
        className="mx-auto max-w-7xl px-6 lg:px-10 pt-16 lg:pt-20"
      >
        <div className="flex items-center gap-3">
          <DollarSign className="h-5 w-5 text-accent" />
          <p className="font-mono-ui text-[11px] uppercase tracking-[0.28em] text-accent">
            // Key Financial Impact
          </p>
        </div>
        <h3 className="mt-3 font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight max-w-3xl">
          Consistent revenue growth and margin improvement — across
          industries and cycles.
        </h3>
        <p className="mt-4 text-muted-foreground max-w-3xl leading-relaxed">
          Throughout my career, I have delivered measurable business impact.
          Selected outcomes:
        </p>

        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {IMPACT.map((it) => (
            <div
              key={it.metric}
              className="border border-border bg-background p-6 lg:p-7 hover:-translate-y-1 transition-transform relative overflow-hidden"
              data-testid={`impact-card-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between">
                <div className="h-10 w-10 border border-border grid place-items-center bg-secondary/60">
                  <it.icon className="h-4 w-4 text-accent" />
                </div>
                <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                  {it.label}
                </p>
              </div>
              <p className="mt-6 font-display font-black text-3xl sm:text-4xl tracking-tighter text-foreground">
                {it.metric}
              </p>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {it.body}
              </p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-sm text-muted-foreground max-w-3xl leading-relaxed border-l border-border pl-4">
          These results were accomplished through strategic go‑to‑market
          execution, team development, and adapting to evolving industry
          conditions.
        </p>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="hairline-ai my-16 lg:my-20" />
      </div>

      {/* ROLES TIMELINE */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                Career timeline
              </p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-ai" /> Medical
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-accent" /> Technology
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-2 w-2 bg-energy" /> Energy
                </li>
              </ul>
              <p className="mt-6 font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                Most recent → foundation
              </p>
            </div>
          </div>

          <div className="lg:col-span-9 space-y-6">
            {ROLES.map((r, i) => (
              <article
                key={r.company}
                data-testid={`experience-role-${i}`}
                className="border border-border bg-background p-6 lg:p-8 hover:-translate-y-1 transition-transform"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 border border-border grid place-items-center bg-secondary/60 shrink-0">
                      <r.icon
                        className={`h-4 w-4 ${
                          r.accent === "accent"
                            ? "text-accent"
                            : r.accent === "energy"
                            ? "text-energy"
                            : "text-ai"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                        {r.tag}
                      </p>
                      <h3 className="mt-1 font-display font-bold text-2xl tracking-tight leading-tight">
                        {r.company}
                      </h3>
                      <p className="text-base text-foreground/90 mt-1">
                        {r.role}
                      </p>
                    </div>
                  </div>
                  <div className="sm:text-right shrink-0">
                    <span className="font-mono-ui text-[11px] uppercase tracking-widest text-accent border border-border px-2 py-1">
                      {r.period}
                    </span>
                  </div>
                </div>

                <p className="mt-6 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {r.body}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  {r.highlights.map((h) => (
                    <span
                      key={h}
                      className="font-mono-ui text-[10px] uppercase tracking-widest px-2 py-1 border border-border text-muted-foreground"
                    >
                      {h}
                    </span>
                  ))}
                </div>

                {r.image && (
                  <div className="mt-6 relative aspect-[16/9] overflow-hidden border border-border bg-secondary">
                    <img
                      src={r.image}
                      alt={r.imageAlt || r.role}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      style={r.imagePosition ? { objectPosition: r.imagePosition } : undefined}
                    />
                    {r.imageCaption && (
                      <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-background/90 to-transparent">
                        <p className="font-mono-ui text-[10px] uppercase tracking-widest text-muted-foreground">
                          {r.imageCaption}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="hairline-ai my-16 lg:my-20" />
      </div>

      {/* KEY THEMES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-20 lg:pb-28">
        <p className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-ai">
          // Key Themes Across My Career
        </p>
        <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight max-w-3xl">
          The through‑lines that show up in every chapter.
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {THEMES.map((t) => (
            <div
              key={t.title}
              className="border border-border p-6 bg-background hover:-translate-y-1 transition-transform"
            >
              <t.icon className="h-5 w-5 text-accent" />
              <h3 className="mt-4 font-display font-bold text-lg">{t.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {t.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
